import { usePersistedState } from "@/lib/persistedFilters";
// Deals tab — per-domain overrides (level, term, contract dates, notes).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Search, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { apiError, thCls, tdCls } from "./engineShared";

export function EngineDeals({ canManage, levels, reloadOverview }) {
  const [q, setQ] = usePersistedState("engine-deals.q", "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [agents, setAgents] = useState([]);
  const [psms, setPsms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/commissions/engine/deals", { params: { q: q || undefined, page, page_size: 50 } }); setData(d); }
    catch (e) { apiError(e, "Could not load deals"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    api.get("/commissions/engine/agents").then(({ data }) => { setAgents(data.items || []); setPsms(data.psms || []); }).catch(() => {});
  }, []);
  const pages = Math.max(1, Math.ceil((data.total || 0) / 50));

  const remove = async (d) => {
    if (!window.confirm(`Delete the deal for ${d.domain}?`)) return;
    try { await api.delete(`/commissions/engine/deals/${d.deal_term_id}`); toast.success("Deal deleted"); load(); reloadOverview?.(); }
    catch (e) { apiError(e); }
  };

  return (
    <div className="space-y-3" data-testid="engine-deals">
      <Card className="border-slate-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} placeholder="Domain, deal, agent or note" className="h-9 pl-8" data-testid="engine-dl-search" />
          </div>
          <Button variant="outline" className="h-9" onClick={() => { setPage(1); load(); }} data-testid="engine-dl-apply">Search</Button>
          <span className="text-xs text-slate-500" data-testid="engine-dl-count">{data.total} deals</span>
          {canManage && <Button className="ml-auto h-9" onClick={() => setEdit({})} data-testid="engine-dl-add"><Plus className="h-4 w-4" /> Add deal</Button>}
        </div>
        <p className="mt-2 text-xs text-slate-500">A deal overrides the agent's level for one customer domain. Term <b>1</b> (month-to-month) forces the standard rate; any longer term forces the contracted rate. It applies to invoices posted after the contract start.</p>
      </Card>
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-dl-table">
            <thead className="bg-slate-50"><tr>
              <th className={thCls}>Domain</th><th className={thCls}>Deal</th><th className={thCls}>Agent</th><th className={thCls}>Level</th>
              <th className={`${thCls} text-right`}>Term</th><th className={thCls}>Contract</th><th className={thCls}>Notes</th><th className={thCls}>Active</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No deals</td></tr>}
              {!loading && data.items.map((d) => (
                <tr key={d.deal_term_id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${d.active ? "" : "opacity-60"}`} data-testid={`engine-dl-row-${d.deal_term_id}`}>
                  <td className={`${tdCls} font-medium text-slate-800`}>{d.domain}</td>
                  <td className={`${tdCls} text-slate-700`}>{d.deal_name || "—"}</td>
                  <td className={`${tdCls} text-slate-600`}>{d.agent || "—"}</td>
                  <td className={tdCls}>{d.level_name || "—"}</td>
                  <td className={`${tdCls} text-right tabular-nums`}>{d.term === 1 ? "M2M" : d.term ? `${d.term} mo` : "—"}</td>
                  <td className={`${tdCls} whitespace-nowrap text-xs text-slate-600`}>{d.contract_start || "—"} → {d.contract_end || "open"}</td>
                  <td className={`${tdCls} max-w-[260px] truncate text-xs text-slate-500`} title={d.notes}>{d.notes}</td>
                  <td className={tdCls}>{d.active ? "yes" : "no"}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>{canManage && (<>
                    <Button variant="ghost" size="sm" onClick={() => setEdit(d)} data-testid={`engine-dl-edit-${d.deal_term_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(d)} className="text-rose-600" data-testid={`engine-dl-del-${d.deal_term_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs text-slate-600">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="engine-dl-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="engine-dl-next">Next</Button>
          </div>
        </div>
      </Card>
      {edit && <DealDialog deal={edit} agents={agents} levels={levels} psms={psms} onClose={() => setEdit(null)}
                           onSaved={() => { setEdit(null); load(); reloadOverview?.(); }} />}
    </div>
  );
}

function DealDialog({ deal, agents, levels, psms, onClose, onSaved }) {
  const isNew = !deal.deal_term_id;
  const [f, setF] = useState({
    domain: deal.domain || "", agent_id: deal.agent_id || "", deal_name: deal.deal_name || "",
    level_id: deal.level_id ? String(deal.level_id) : "", psm_id: deal.psm_id ? String(deal.psm_id) : "",
    term: deal.term ?? 1, contract_start: deal.contract_start || "", contract_end: deal.contract_end || "",
    quote: deal.quote || "", notes: deal.notes || "", active: deal.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, term: Number(f.term || 1), level_id: f.level_id ? Number(f.level_id) : null, psm_id: f.psm_id ? Number(f.psm_id) : null,
                     contract_start: f.contract_start || null, contract_end: f.contract_end || null };
      if (isNew) await api.post("/commissions/engine/deals", body);
      else await api.put(`/commissions/engine/deals/${deal.deal_term_id}`, body);
      toast.success(isNew ? "Deal added" : "Deal saved");
      onSaved();
    } catch (e) { apiError(e, "Could not save the deal"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="engine-deal-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add deal" : "Edit deal"}</DialogTitle>
          <DialogDescription>Applies from the next sync; resync a month to recompute it.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Domain</Label><Input value={f.domain} onChange={(e) => set("domain", e.target.value)} placeholder="customer.ca" className="h-9" data-testid="engine-deal-domain" /></div>
          <div><Label>Deal name</Label><Input value={f.deal_name} onChange={(e) => set("deal_name", e.target.value)} className="h-9" data-testid="engine-deal-name" /></div>
          <div className="col-span-2"><Label>Agent</Label>
            <Select value={f.agent_id || "none"} onValueChange={(v) => set("agent_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-deal-agent"><SelectValue placeholder="Pick the agent" /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="none">—</SelectItem>{agents.map((a) => <SelectItem key={a.agent_id} value={a.agent_id}>{a.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Level</Label>
            <Select value={f.level_id || "none"} onValueChange={(v) => set("level_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-deal-level"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Agent's level</SelectItem>{levels.map((l) => <SelectItem key={l.level_id} value={String(l.level_id)}>{l.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>PSM</Label>
            <Select value={f.psm_id || "none"} onValueChange={(v) => set("psm_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-deal-psm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{psms.map((p) => <SelectItem key={p.psm_id} value={String(p.psm_id)}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Term (months, 1 = month-to-month)</Label><Input type="number" min="1" value={f.term} onChange={(e) => set("term", e.target.value)} className="h-9" data-testid="engine-deal-term" /></div>
          <div><Label>Quote</Label><Input value={f.quote} onChange={(e) => set("quote", e.target.value)} className="h-9" data-testid="engine-deal-quote" /></div>
          <div><Label>Contract start</Label><Input type="date" value={f.contract_start} onChange={(e) => set("contract_start", e.target.value)} className="h-9" data-testid="engine-deal-start" /></div>
          <div><Label>Contract end</Label><Input type="date" value={f.contract_end} onChange={(e) => set("contract_end", e.target.value)} className="h-9" data-testid="engine-deal-end" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="engine-deal-notes" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="engine-deal-active" /> Active</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="engine-deal-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !f.domain.trim() || !f.agent_id} data-testid="engine-deal-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
